import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import { Cpu, Loader2, Plus, Save, Trash2 } from "lucide-react";

const CLASSES = [
  { id: "lourde", label: "Tâches lourdes (doctrine, Friction Gap, Bras Armé, graphe)" },
  { id: "quotidienne", label: "Tâches quotidiennes (copilote, chat, résumés, enrichissement)" },
  { id: "masse", label: "Opérations de masse (radar, crawler, classification, matching)" },
];
const PROVIDERS = ["anthropic", "openai", "gemini"];

export default function ByokConfigPanel() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/ultra/llm-byok-config")
      .then((r) => setCfg(JSON.parse(JSON.stringify(r.data.config))))
      .catch((e) => notify.error(fmtError(e)));
  }, []);

  if (!cfg) return null;

  const setChain = (cls, chain) => setCfg({ ...cfg, classes: { ...cfg.classes, [cls]: chain } });
  const updRow = (cls, i, col, v) => {
    const chain = cfg.classes[cls].map((r, j) => (j === i ? r.map((c, k) => (k === col ? v : c)) : r));
    setChain(cls, chain);
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/ultra/llm-byok-config", cfg);
      notify.success("Configuration IA enregistrée — effective sous 60 s.");
    } catch (e) { notify.error(fmtError(e)); } finally { setBusy(false); }
  };

  return (
    <section className="noam-card p-5 space-y-5 mt-6" data-testid="byok-config-panel">
      <header className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 text-[10px] font-semibold tracking-[0.18em] uppercase text-[#7F77DD]">
            <Cpu className="w-3 h-3" /> Modèles IA par classe de tâche &amp; politique BYOK
          </div>
          <p className="text-[11px] text-zinc-500 mt-1">
            Chaîne de modèles essayés dans l'ordre. Clé cliente utilisée si configurée
            (perso &gt; org), sinon clé NOAM — avec alerte si le client est payant,
            blocage si son plan est listé ci-dessous.
          </p>
        </div>
        <button onClick={save} disabled={busy}
                className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded bg-[#1D9E75]/15 text-[#3fcf9e] border border-[#1D9E75]/30 hover:bg-[#1D9E75]/25 disabled:opacity-40"
                data-testid="byok-config-save">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          Enregistrer
        </button>
      </header>

      {CLASSES.map(({ id, label }) => (
        <div key={id} data-testid={`byok-class-${id}`}>
          <div className="text-xs text-zinc-300 mb-1.5">{label}</div>
          {(cfg.classes[id] || []).map((row, i) => (
            <div key={i} className="flex items-center gap-2 mb-1">
              <span className="text-[10px] text-zinc-600 w-4">{i + 1}.</span>
              <select value={row[0]} onChange={(e) => updRow(id, i, 0, e.target.value)}
                      className="bg-black/30 border border-white/10 rounded px-2 py-1 text-xs text-zinc-200">
                {PROVIDERS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
              <input value={row[1]} onChange={(e) => updRow(id, i, 1, e.target.value)}
                     className="flex-1 bg-black/30 border border-white/10 rounded px-2 py-1 text-xs text-zinc-200 font-mono"
                     placeholder="identifiant du modèle" />
              <input value={row[2]} onChange={(e) => updRow(id, i, 2, e.target.value)}
                     className="w-40 bg-black/30 border border-white/10 rounded px-2 py-1 text-xs text-zinc-400"
                     placeholder="libellé" />
              <button onClick={() => setChain(id, cfg.classes[id].filter((_, j) => j !== i))}
                      disabled={cfg.classes[id].length <= 1}
                      className="text-zinc-600 hover:text-[#E24B4A] disabled:opacity-30">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
          <button onClick={() => setChain(id, [...cfg.classes[id], ["openai", "", ""]])}
                  className="text-[11px] text-zinc-500 hover:text-white inline-flex items-center gap-1 mt-0.5">
            <Plus className="w-3 h-3" /> Ajouter un modèle de secours
          </button>
        </div>
      ))}

      <div className="grid md:grid-cols-3 gap-4 pt-2 border-t border-white/5">
        <div>
          <div className="text-[11px] text-zinc-400 mb-1">Motifs « lourde » (session_id contient)</div>
          <input value={(cfg.class_rules?.lourde || []).join(", ")}
                 onChange={(e) => setCfg({ ...cfg, class_rules: { ...cfg.class_rules, lourde: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) } })}
                 className="w-full bg-black/30 border border-white/10 rounded px-2 py-1 text-xs text-zinc-300" />
        </div>
        <div>
          <div className="text-[11px] text-zinc-400 mb-1">Motifs « masse »</div>
          <input value={(cfg.class_rules?.masse || []).join(", ")}
                 onChange={(e) => setCfg({ ...cfg, class_rules: { ...cfg.class_rules, masse: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) } })}
                 className="w-full bg-black/30 border border-white/10 rounded px-2 py-1 text-xs text-zinc-300" />
        </div>
        <div>
          <div className="text-[11px] text-zinc-400 mb-1">Plans bloqués sans clé cliente</div>
          <input value={(cfg.blocked_plans || []).join(", ")}
                 onChange={(e) => setCfg({ ...cfg, blocked_plans: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                 className="w-full bg-black/30 border border-white/10 rounded px-2 py-1 text-xs text-zinc-300"
                 data-testid="byok-blocked-plans" />
          <div className="text-[10px] text-zinc-600 mt-1">
            Constante Partners (interne) n'est jamais bloquée ni alertée.
          </div>
        </div>
      </div>
    </section>
  );
}
